/**
 * Extract a user-friendly error message from standardized error responses
 * Now supports both the new standardized format and legacy FastAPI formats
 */
export function extractErrorMessage(error: any): string {
  const defaultMsg = "Request failed";
  const data = error?.response?.data;

  if (!data) {
    return error?.message || defaultMsg;
  }

  if (typeof data === "string") {
    return data;
  }

  // New standardized error response format
  if (data.error && data.message) {
    return data.message;
  }

  // Legacy FastAPI shape: { detail: "..." } or { detail: [{ loc, msg, type }, ...] }
  const detail = (data as any).detail;
  if (detail) {
    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msgs = detail.map((d: any) => d?.msg || d?.message).filter(Boolean);
      if (msgs.length > 0) {
        return Array.from(new Set(msgs)).join("; ");
      }
    }
  }

  // Some APIs use { message: "..." }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (data as any).message === "string") {
    return (data as any).message;
  }

  try {
    return JSON.stringify(data);
  } catch {
    return defaultMsg;
  }
}

/**
 * Extract structured error information from standardized error responses
 */
export function extractErrorInfo(error: any): {
  message: string;
  code?: string;
  error?: string;
  details?: any;
  retryAfter?: number;
  statusCode?: number;
} {
  const data = error?.response?.data;
  const statusCode = error?.response?.status;

  if (!data) {
    return {
      message: error?.message || "Request failed",
      statusCode,
    };
  }

  // New standardized error response format
  if (data.error && data.message) {
    return {
      message: data.message,
      code: data.code,
      error: data.error,
      details: data.details,
      retryAfter: data.retry_after,
      statusCode,
    };
  }

  // Legacy format fallback
  return {
    message: extractErrorMessage(error),
    statusCode,
  };
}

/**
 * Get user-friendly error messages based on error codes and types
 */
export function getFriendlyErrorMessage(error: any): string {
  const errorInfo = extractErrorInfo(error);
  const { message, code, error: errorType, statusCode } = errorInfo;

  // Handle specific error codes with user-friendly messages
  switch (code) {
    case "INVALID_CREDENTIALS":
      return "Invalid username or password. Please check your credentials and try again.";
    case "NO_ACTIVE_SESSION":
      return "Your session has expired. Please log in again.";
    case "INVALID_SESSION":
      return "Your session is invalid. Please log in again.";
    case "UNAUTHENTICATED":
      return "You are not logged in. Please log in to continue.";
    case "RATE_LIMIT_EXCEEDED":
      return "Too many login attempts. Please wait a moment before trying again.";
    case "USER_NOT_FOUND":
      return "User account not found. Please check your username.";
    case "ACCOUNT_DISABLED":
      return "Your account has been disabled. Please contact support.";
    case "INSUFFICIENT_PERMISSIONS":
      return "You don't have permission to access this resource.";
    case "VALIDATION_ERROR":
      return "Please check your input and try again.";
    case "NOT_FOUND":
      return "The requested resource was not found.";
    case "UNAUTHORIZED":
      return "You are not authorized to perform this action.";
    case "FORBIDDEN":
      return "Access denied. You don't have permission for this action.";
    case "INTERNAL_SERVER_ERROR":
      return "A server error occurred. Please try again later.";
    default:
      // Handle error types if no specific code
      switch (errorType) {
        case "Unauthenticated":
          return "You are not logged in. Please log in to continue.";
        case "Unauthorized":
          return "Authentication required. Please log in.";
        case "Forbidden":
          return "Access denied. You don't have permission for this action.";
        case "NotFound":
          return "The requested resource was not found.";
        case "ValidationError":
          return "Please check your input and try again.";
        case "TooManyRequests":
          return "Too many requests. Please wait before trying again.";
        case "InternalServerError":
          return "A server error occurred. Please try again later.";
        default:
          // Handle status codes if no specific error type
          if (statusCode === 401) {
            return "You are not logged in. Please log in to continue.";
          }
          if (statusCode === 403) {
            return "Access denied. You don't have permission for this action.";
          }
          if (statusCode === 404) {
            return "The requested resource was not found.";
          }
          if (statusCode === 429) {
            return "Too many requests. Please wait before trying again.";
          }
          if (statusCode && statusCode >= 500) {
            return "A server error occurred. Please try again later.";
          }

          // Return the original message if it's user-friendly, otherwise provide a generic one
          if (
            message &&
            !message.includes("Request failed") &&
            !message.includes("status code")
          ) {
            return message;
          }
          return "An error occurred. Please try again.";
      }
  }
}
